import { useEffect, useRef, useState } from "react";
import { createRoot } from 'react-dom/client';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from '@vis.gl/react-google-maps';
import MoonLoader from "react-spinners/MoonLoader";

const App = () => {
    const [partners, setPartners] = useState<any[]>([]);
    const [locations, setLocations] = useState<PartnerLocation[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [radius, setRadius] = useState('25');
    const [page, setPage] = useState(1);
    const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number; } | null>(null);
    const [pagination, setPagination] = useState<{ page: number, perPage: number; lastPage: number; totalPartners: number }>({ page: 0, perPage: 25, lastPage: 0, totalPartners: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const autocompleteErrorRef = useRef(null);

    const mapId = '3e6ee008bc8b225a1d2b6be3';
    const apiKey = 'AIzaSyDdL7PQS7BiwI0ga7fEfWDmQT9jowwNlKU';

    useEffect(() => {
        console.log('fetching data with params:', { searchLocation, radius, page });
        if (!searchLocation) return;

        const fetchData = async () => {
            const fetchUrl = new URL(
                '/wp-json/geminimade/v1/partner_stores',
                window.location.protocol + window.location.hostname
            );
            fetchUrl.searchParams.append('lat', searchLocation?.lat?.toString() || '');
            fetchUrl.searchParams.append('lng', searchLocation?.lng?.toString() || '');
            fetchUrl.searchParams.append('filter', 'all');
            fetchUrl.searchParams.append('brand', 'signage');
            fetchUrl.searchParams.append('distance', radius);
            fetchUrl.searchParams.append('pg', page.toString());
            try {
                const response = await fetch(fetchUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();

                let dbResults: PartnerLocation[] = [];

                result.posts.forEach((store: any) => {
                    dbResults.push({ id: parseInt(store.id), name: store.name, lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) });
                });

                setPartners(result.posts);
                setPagination({ page: result.pagination.page, perPage: result.pagination.per_page, lastPage: result.pagination.last_page, totalPartners: result.pagination.total_posts });
                setLocations(dbResults);
                setHasSearched(true);
                setLoading(false);
            } catch (err) {
            } finally {
            }
        };
        fetchData();
    }, [searchLocation, radius, page]);

    function handleRadiusChange(e: React.ChangeEvent<HTMLSelectElement>) {
        setPage(1);
        setRadius(e.target.value);
        setHasSearched(false);
        setLoading(true);
        setPartners([]);
        setPagination({ page: 0, perPage: 0, lastPage: 0, totalPartners: 0 });
        setLocations([]);
    }


    function handlePage(e: React.MouseEvent<HTMLButtonElement>) {
        if (loading) {
            return;
        }
        setLoading(true);
        setPage(parseInt(e.currentTarget.value));
    }


    return <>
        <APIProvider apiKey={apiKey} onLoad={() => console.log('Maps API has loaded.')}>
            <div className="authorized-partner-new">
                <div className="authorized-partner-wrapper container">
                    <div className="row authorized-partner-filter-wrapper">
                        <div className="authorized-partner-filter" style={{ display: "inline", paddingBottom: error ? "28px" : "22px" }}>
                            <div className="input-group">
                                <label htmlFor="autocomplete">Find an authorized reseller near this ZIP code:</label>
                                <AutoComplete setPartners={setPartners} setLocations={setLocations} setHasSearched={setHasSearched} setSearchLocation={setSearchLocation} setPagination={setPagination} autocompleteErrorRef={autocompleteErrorRef} setLoading={setLoading} loading={loading} setError={setError} setPage={setPage} />
                                <div className="input-group-append">
                                    <select
                                        id="distance"
                                        className="custom-select"
                                        defaultValue="25"
                                        aria-label="Searching distance from city"
                                        onChange={handleRadiusChange}
                                        value={radius}
                                    >
                                        <option value="5">5 miles</option>
                                        <option value="10">10 miles</option>
                                        <option value="15">15 miles</option>
                                        <option value="25">25 miles</option>
                                        <option value="50">50 miles</option>
                                        <option value="100">100 miles</option>
                                    </select>
                                </div>
                            </div>
                            <div ref={autocompleteErrorRef} className="autocomplete-error d-none">Zip code/Postal code does not exist</div>
                        </div>
                    </div>
                    <div className="authorized-partner-results-wrapper">
                        <div className="authorized-partner-results-header">
                            <PaginationComponent page={page} pagination={pagination} handlePage={handlePage} />
                            <div id="authorized-partner-results-top" className="authorized-partner-results-heading-wrapper row">
                                <div className="authorized-partner-results-heading col-12 col-md-6 offset-md-6">
                                    {hasSearched ? (
                                        locations.length > 0 ?
                                            (
                                                <>
                                                    <h2 style={{ fontSize: '1.5rem' }}>
                                                        <span style={{ fontSize: '1.5rem' }}>
                                                            {(pagination.page - 1) * pagination.perPage} - {Math.min(pagination.page * pagination.perPage, pagination.totalPartners)}
                                                            {' of '}
                                                        </span>
                                                        {pagination.totalPartners}
                                                        {locations.length === 1
                                                            ? ' Result'
                                                            : ' Results'}
                                                    </h2>
                                                    <span></span>
                                                </>
                                            )
                                            : <h2>No results</h2>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                        <Partnerlist mapId={mapId} locations={locations} loading={loading} hasSearched={hasSearched} partners={partners} />
                        <div className="authorized-partner-results-footer row">
                            <PaginationComponent page={page} pagination={pagination} handlePage={handlePage} />
                        </div>
                    </div>
                </div>
            </div>
        </APIProvider >
    </>
}


const Partnerlist = ({ mapId, locations, loading, hasSearched, partners }: { mapId: string; locations: PartnerLocation[]; loading: boolean; hasSearched: boolean; partners: any[]; }) => {
    return <div className="authorized-partner-results-body row">
        <div className="map-container col-lg-6">
            <MapContent mapId={mapId} locations={locations} />
        </div>
        <div className="authorized-partner-results col-lg-6">
            {loading && (
                <div className="spinner-backdrop">
                    <MoonLoader
                        size={80}
                        cssOverride={{
                            margin: '0 auto'
                        }}
                        color={"#000000"}
                        loading={true}
                        speedMultiplier={1}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            {
                hasSearched ? (
                    partners.length ?
                        (
                            loading ? '' :
                                partners.map((partner: any) => (
                                    <div className="authorized-partner-result-item">
                                        < div className="card">
                                            <div className="card-body">
                                                <span className="name">{partner.name}</span><br />
                                                <img src={'/images/icon-location.svg'} alt="Location" />
                                                <a href={partner.map} target="_blank" style={{ textDecoration: "underline", display: 'inline' }}>
                                                    <span className="address-1" style={{ marginLeft: ".4rem" }}>{partner.address_1} </span>
                                                    <span className="city-state">{partner.city}, {partner.state} {partner.postal_code}</span><br />
                                                </a>
                                                <img src={'/images/icon-phone.svg'} alt="Phone" />
                                                <span className="phone" style={{ marginLeft: ".4rem" }}>{formatPhoneNumber(partner.phone)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                        )
                        : 'No results found for the mile range selected.'

                ) : (
                    loading ? '' : <p className="no-results-info" style={{ paddingLeft: '1rem' }}>Search for an authorized reseller by entering a ZIP code above.</p>
                )
            }
        </div>
    </div>;
}

const AutoComplete = (
    { setPartners, setLocations, setHasSearched, setSearchLocation, setPagination, autocompleteErrorRef, setLoading, loading, setError, setPage }:
        { setPartners: (partners: any[]) => void; setLocations: (locations: PartnerLocation[]) => void; setHasSearched: (hasSearched: boolean) => void; setSearchLocation: (searchLocation: { lat: number; lng: number }) => void; setPagination: (pagination: { page: number; perPage: number; lastPage: number; totalPartners: number }) => void, autocompleteErrorRef: any, setLoading: (loading: boolean) => void, loading: boolean, setError: (error: boolean) => void, setPage: (page: number) => void }) => {
    const map = useMap();
    const handlePlaceSelect = async (place: google.maps.places.PlaceResult | null) => {
        if (!place?.geometry?.location || !map) return;
        map.panTo(place.geometry.location);
        map.setZoom(10);

        const searchLat = place.geometry.location.lat();
        const searchLng = place.geometry.location.lng();
        setSearchLocation({ lat: searchLat, lng: searchLng });
        setHasSearched(false);
        setLoading(true);
        setPartners([]);
        setPage(1);
        setPagination({ page: 0, perPage: 0, lastPage: 0, totalPartners: 0 });
        setLocations([]);
    };

    return <PlaceAutocomplete onPlaceSelect={handlePlaceSelect} autocompleteErrorRef={autocompleteErrorRef} loading={loading} setError={setError} />
}

const MapContent = ({ mapId, locations }: { mapId: string; locations: PartnerLocation[]; }) => {
    const CustomSvgMarker = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="28.98" height="28.75" viewBox="0 0 28.98 28.75">
            <defs>
                <style>{`.a{fill:#e3173e;}.b{fill:#fff;}`}</style>
            </defs>
            <path className="a" d="M21.61,15V.75H7.36V15H1.81L14.49,27.69,27.17,15Z" />
            <path className="b" d="M14.49,28.75,0,14.26H6.61V0H22.36V14.26H29Zm-10.87-13L14.49,26.63,25.36,15.76h-4.5V1.5H8.11V15.76Z" />
        </svg>
    );
    return <>
        <Map
            mapId={mapId}
            maxZoom={16}
            defaultCenter={{ lat: 45, lng: -97 }}
            mapTypeControl={false}
            zoomControl={true}
            streetViewControl={false}
            defaultZoom={3}
        >
            {locations.map((loc) => (
                <AdvancedMarker
                    key={loc.id}
                    position={{ lat: loc.lat, lng: loc.lng }}
                >
                    <CustomSvgMarker />
                </AdvancedMarker>
            ))}
        </Map>
    </>
}

interface PlaceAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    autocompleteErrorRef: any;
    loading: boolean;
    setError: any;
}

const PlaceAutocomplete = ({ onPlaceSelect, autocompleteErrorRef, loading, setError }: PlaceAutocompleteProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options = {
            types: ['postal_code'],
            fields: ['geometry', 'name', 'formatted_address'],
            componentRestrictions: {
                country: ['us', 'ca'],
            },
        };
        setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            const place = placeAutocomplete.getPlace();
            onPlaceSelect(place);
            if (!place.formatted_address) {
                if (String(inputRef.current?.value).length) {
                    setError(true);
                    inputRef.current?.style.setProperty('border-color', '#df2727');
                    autocompleteErrorRef.current?.classList.add('d-block');
                    return;
                }
            } else {
                setError(false);
                autocompleteErrorRef.current?.classList.add('d-none');
                autocompleteErrorRef.current?.classList.remove('d-block');
                inputRef.current?.style.setProperty('border-color', '#ddd');
            }
        });

        return () => {
            if (placeAutocomplete) {
                google.maps.event.clearInstanceListeners(placeAutocomplete);
            }
        };
    }, [onPlaceSelect, placeAutocomplete]);

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                const pacContainer = document.querySelector('.pac-container');
                const isOpen = pacContainer && pacContainer.querySelectorAll('.pac-item').length > 0;
                if (isOpen && !document.querySelector('.pac-item-selected')) {
                    const downEvent = new KeyboardEvent('keydown', {
                        key: 'ArrowDown',
                        code: 'ArrowDown',
                        keyCode: 40,
                        which: 40,
                        bubbles: true,
                    });
                    const enterKeyEvent = new KeyboardEvent('keydown', {
                        key: 'Enter',
                        code: 'Enter',
                        keyCode: 13,
                        which: 13,
                        bubbles: false,
                    });
                    inputRef.current?.dispatchEvent(downEvent);
                    inputRef.current?.dispatchEvent(enterKeyEvent);
                } else {
                }
            }
        };

        input.addEventListener('keydown', handleKeyDown, true);

        return () => {
            input.removeEventListener('keydown', handleKeyDown, true);
        };
    }, []);

    return (
        <input id="autocomplete"
            className="form-control pac-target-input"
            ref={inputRef}
            placeholder={'Enter a ZIP code'}
            type="text"
            disabled={loading}
        />
    );
};

const PaginationComponent = (
    { page, pagination, handlePage }:
        { page: number; pagination: any; handlePage: (e: React.MouseEvent<HTMLButtonElement>) => void }) => {
    const PAGE_WINDOW = 10;
    const startPage = Math.max(1, page - Math.floor(PAGE_WINDOW / 2));
    const endPage = Math.min(pagination.lastPage, startPage + PAGE_WINDOW - 1);
    const pages = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
    return <div className="authorized-partner-pagination col-12">
        {pagination.lastPage > 1 ? (
            <nav className="pagination" aria-label="Pagination">
                <button value={page - 1} onClick={handlePage} disabled={page > 1 ? false : true}>
                    Previous
                </button>
                {pages.map((pageNum) => (
                    <button
                        key={pageNum}
                        value={pageNum}
                        onClick={handlePage}
                        className={pageNum === page ? 'current' : ''}
                        aria-current={pageNum === page ? 'page' : undefined}
                    >
                        {pageNum}
                    </button>
                ))}
                <button value={page + 1} onClick={handlePage} disabled={page < pagination.lastPage ? false : true}>
                    Next
                </button>
            </nav>
        ) : (
            ''
        )}
    </div>;
};

function whereToBuyInit() {
    const authorizedPartner = createRoot(document.getElementById('authorized-partner-new'));
    authorizedPartner.render(
        <App />
    );
}

function formatPhoneNumber(phoneNumberString) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }

    return phoneNumberString;
}

interface PartnerLocation {
    id: string | number;
    name: string;
    lat: number;
    lng: number;
}

export { whereToBuyInit };
